// Silk Network — GA4 Funnel Analytics
// Fires dataLayer + gtag events for the 4 discovery-funnel event types:
//   newsletter_cta_click   — newsletter/Subscribe/Stay Connected CTAs
//   business_outbound_click — outbound clicks to business/partner URLs
//   perk_cta_click         — clicks on coupon/perk CTAs
//   story_crosslink_click  — clicks linking to other Silk Network stories
//
// Event spec: GST-1211
//
// Uses event delegation on document (capture phase) — no mutation observers.
// All link clicks pass through a single handler; early-return per category.

(function() {
    'use strict';

    // ── helpers ──────────────────────────────────────────────────────────

    function getPostSlug() {
        var el = document.querySelector('[data-post-slug]');
        return el ? el.getAttribute('data-post-slug') : '';
    }

    function getPageUrl() {
        return window.location.href;
    }

    function getLinkText(el) {
        return (el.textContent || '').trim();
    }

    /** Classify an external hostname into ig / website / newsletter / internal */
    function classifyLinkType(hostname) {
        hostname = hostname.replace(/^www\./, '').toLowerCase();
        if (hostname === 'instagram.com') return 'ig';
        if (hostname === 'hello.silknetwork.us') return 'newsletter';
        if (hostname.match(/(silknetwork|passportly)/)) return 'internal';
        return 'website';
    }

    /** Extract a probable business name from a link element */
    function guessBusinessName(link) {
        var text = getLinkText(link);
        if (text && text.length > 1 && text.length < 80) return text;
        var href = link.getAttribute('href') || '';
        var match = href.match(/(?:instagram\.com\/|https?:\/\/)([^\/.]+)/);
        return match ? match[1] : '';
    }

    /** Derive a cta_position value based on ancestor class hints */
    function getCtaPosition(link) {
        var walk = link;
        while (walk && walk !== document.body) {
            var cls = walk.className || '';
            if (typeof cls === 'string') {
                if (cls.indexOf('landing') !== -1 || cls.indexOf('hero') !== -1) return 'hero';
                if (cls.indexOf('closing') !== -1 || cls.indexOf('cta') !== -1) return 'closing-cta';
                if (cls.indexOf('footer') !== -1) return 'footer';
                if (cls.indexOf('site-nav') !== -1 || cls.indexOf('mobile-menu') !== -1) return 'navigation';
                if (cls.indexOf('callout') !== -1) return 'module';
                if (cls.indexOf('button-card') !== -1) return 'post-footer';
            }
            walk = walk.parentElement;
        }
        var linkClass = link.className || '';
        if (typeof linkClass === 'string' && linkClass.indexOf('kg-btn') !== -1) return 'post-footer';
        return 'inline';
    }

    /** Check if an element (or its parent chain) contains perk-related text */
    function isPerkContext(link) {
        var walk = link;
        var depth = 0;
        while (walk && depth < 10) {
            var txt = (walk.textContent || '').toLowerCase();
            if (txt.indexOf('silknetwork') !== -1 || txt.indexOf('perk') !== -1 || txt.indexOf('coupon') !== -1 || txt.indexOf('code') !== -1) {
                if (txt.indexOf('%') !== -1 || txt.indexOf('off') !== -1 || txt.indexOf('discount') !== -1 || txt.indexOf('free') !== -1) {
                    return true;
                }
                if (txt.indexOf('silknetwork') !== -1) return true;
            }
            walk = walk.parentElement;
            depth++;
        }
        return false;
    }

    /** Extract a target story slug from a cross-link URL */
    function extractTargetSlug(href) {
        var match = href.match(/\/([a-z0-9-]+)\/?$/);
        if (match) {
            var slug = match[1];
            if (['blog', 'contact', 'austin', 'about', 'healthz'].indexOf(slug) === -1) {
                return slug;
            }
        }
        return '';
    }

    /** Append UTM params to an outbound URL if not already present */
    function appendUtm(href, source, medium, campaign) {
        try {
            var url = new URL(href, window.location.href);
            if (!url.searchParams.get('utm_source')) {
                url.searchParams.set('utm_source', source);
            }
            if (!url.searchParams.get('utm_medium')) {
                url.searchParams.set('utm_medium', medium);
            }
            if (!url.searchParams.get('utm_campaign')) {
                url.searchParams.set('utm_campaign', campaign);
            }
            return url.toString();
        } catch (e) {
            return href;
        }
    }

    /** Fire both dataLayer.push and gtag event for GA4 */
    function pushEvent(eventName, params) {
        if (typeof window.dataLayer === 'undefined') {
            window.dataLayer = [];
        }
        var payload = { event: eventName, ...params };
        window.dataLayer.push(payload);

        // Also fire via gtag if available — ensures GA4 custom event dimension
        if (typeof window.gtag === 'function') {
            window.gtag('event', eventName, params);
        }

        // Debug logging — visible in browser devtools console
        if (typeof console !== 'undefined' && console.log) {
            console.log('[Silk Analytics]', eventName, params);
        }
    }

    // ── main click handler ──────────────────────────────────────────────

    document.addEventListener('click', function(e) {
        var link = e.target.closest ? e.target.closest('a[href]') : null;
        if (!link) return;

        var href = link.getAttribute('href');
        if (!href || href === '#' || href.startsWith('javascript:')) return;

        var text = getLinkText(link);
        var postSlug = getPostSlug();
        var pageUrl = getPageUrl();

        // Resolve relative URLs
        var a = document.createElement('a');
        a.href = href;
        var isRelative = !href.match(/^https?:\/\//);
        var hostname = isRelative ? window.location.hostname : a.hostname;
        var isExternal = hostname !== window.location.hostname;

        // ── 1. newsletter_cta_click ─────────────────────────────────────
        if (
            hostname === 'hello.silknetwork.us' ||
            text.match(/^subscribe$/i) ||
            text.match(/^stay connected/i) ||
            href.indexOf('#/portal') !== -1 ||
            href.indexOf('/members/') !== -1
        ) {
            pushEvent('newsletter_cta_click', {
                story_slug: postSlug,
                cta_position: getCtaPosition(link),
                page_url: pageUrl
            });
            return;
        }

        // ── 2. story_crosslink_click ────────────────────────────────────
        var isCurrentPage = false;
        if (isRelative) {
            var currentPath = window.location.pathname.replace(/\/$/, '');
            var linkPath = href.replace(/\/$/, '');
            isCurrentPage = currentPath === linkPath || currentPath + '/' === linkPath;
        } else if (!isExternal) {
            var currentPath2 = window.location.pathname.replace(/\/$/, '');
            var linkPath2 = a.pathname.replace(/\/$/, '');
            isCurrentPage = currentPath2 === linkPath2;
        }

        if (!isExternal && !isCurrentPage && !href.match(/^\/?#/)) {
            var targetSlug = extractTargetSlug(href);
            var storyPath = isRelative ? href : a.pathname;
            if (targetSlug || (storyPath.match(/^\/[a-z0-9-]+\/?$/) && !storyPath.match(/^\/(blog|contact|austin|about|healthz)/))) {
                pushEvent('story_crosslink_click', {
                    source_story: postSlug,
                    target_story: targetSlug || storyPath.replace(/^\/|\/$/g, ''),
                    page_url: pageUrl
                });
                return;
            }
        }

        // ── 3. perk_cta_click ───────────────────────────────────────────
        var isPerk = isPerkContext(link);
        if (isPerk) {
            pushEvent('perk_cta_click', {
                story_slug: postSlug,
                business_name: guessBusinessName(link),
                coupon_code: 'SILKNETWORK',
                page_url: pageUrl
            });
            return;
        }

        // ── 4. business_outbound_click ──────────────────────────────────
        if (isExternal) {
            var linkType = classifyLinkType(hostname);
            // Skip internal/newsletter — already handled or not business
            if (linkType === 'newsletter' || linkType === 'internal') return;

            // Append UTM params to outbound business links
            var utmSource = 'silknetwork-blog';
            var utmMedium = linkType === 'ig' ? 'social' : 'referral';
            var utmCampaign = postSlug || 'story-outbound';
            var enrichedHref = appendUtm(href, utmSource, utmMedium, utmCampaign);

            // Update the link href so the browser navigates to the UTM-enriched URL
            if (enrichedHref !== href) {
                link.setAttribute('href', enrichedHref);
            }

            pushEvent('business_outbound_click', {
                business_name: guessBusinessName(link),
                link_type: linkType,
                story_slug: postSlug,
                destination_url: enrichedHref,
                page_url: pageUrl
            });
            return;
        }
    }, true); // useCapture = true — catches clicks before Ghost handlers

})();
